/**
 * MergeFieldPicker — searchable dropdown of {{key}} merge fields filtered by context.
 * Calls onPick(`{{key}}`) when a field is selected.
 */
import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, Tags } from 'lucide-react';

const MergeFieldPicker = ({ groups = [], onPick, label = 'Insert Merge Field' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return groups;
    const q = query.toLowerCase();
    return groups
      .map(g => ({
        ...g,
        fields: (g.fields || []).filter(f =>
          f.key.toLowerCase().includes(q) || (f.label || '').toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.fields.length > 0);
  }, [groups, query]);

  const handlePick = (key) => {
    onPick?.(`{{${key}}}`);
    // keep dropdown open so the user can insert several in a row
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md hover:bg-blue-100"
        data-testid="merge-field-picker-toggle"
      >
        <Tags size={14} className="mr-1.5" />
        {label}
        <ChevronDown size={14} className={`ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute z-20 mt-1 right-0 w-96 max-h-[420px] overflow-hidden bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col"
            data-testid="merge-field-picker-panel"
          >
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search fields..."
                  className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                  data-testid="merge-field-picker-search"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 && (
                <div className="p-4 text-center text-sm text-gray-500">No matching fields</div>
              )}
              {filtered.map((g, idx) => (
                <div key={`${g.module}-${g.section}-${idx}`} className="border-b border-gray-100 last:border-0">
                  <div className="px-3 py-1.5 bg-gray-50 sticky top-0 text-[11px] uppercase tracking-wider text-gray-600 font-semibold">
                    {g.module} · {g.section}
                  </div>
                  {(g.fields || []).map(f => (
                    <button
                      type="button"
                      key={f.key}
                      onClick={() => handlePick(f.key)}
                      className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center justify-between text-sm"
                      data-testid={`merge-field-${f.key}`}
                    >
                      <span className="text-gray-800 truncate">{f.label}</span>
                      <span className="text-[11px] font-mono text-blue-600 ml-3 flex-shrink-0">{`{{${f.key}}}`}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MergeFieldPicker;
